import * as createProperties from 'store/actions/properties/createProperties';
import * as deleteProperties from 'store/actions/properties/deleteProperties';
import * as fetchProperties from 'store/actions/properties/fetchProperties';
import * as types from 'store/actions/types';
import * as updateProperties from 'store/actions/properties/updateProperties';
import apiClient from 'clients/apiClient';
import config from 'helpers/config';
import utils from 'helpers/utils';
import { put, takeEvery } from 'redux-saga/effects';
import { showNotification } from 'store/actions/uiActions';

function* fetchPropertiesAsync({ payload }) {
    try {
        const url = `${ config.getApiBaseUrl() }/properties`;

        const response = yield apiClient.read(url, payload);

        yield put(fetchProperties.fetchPropertiesSuccess(response));
    } catch (error) {
        yield put(fetchProperties.fetchPropertiesFailure(error));
    }
}

function* fetchPropertyAsync({ payload }) {
    const {
        id,
        onError,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/properties/${ id }`;

        // Fetch the data.
        const response = yield apiClient.read(url);

        // Send the data to the property.
        yield put(fetchProperties.fetchPropertySuccess(response));

        // Trigger the localisations of that propety.
        yield put(fetchProperties.fetchPropertyLocalisation(payload));
    } catch (error) {
        // Trigger the error action.
        yield put(fetchProperties.fetchPropertyFailure(error));

        if (utils.isFunction(onError)) {
            onError(error);
        }
    }
}

function* fetchPropertyLocalisationAsync({ payload }) {
    const {
        id,
        onError,
        onSuccess,
    } = payload;

    try {
        const url = `${ config.getApiBaseUrl() }/properties/${ id }/localisation`;

        const response = yield apiClient.read(url);

        yield put(fetchProperties.fetchPropertyLocalisationSuccess(payload, response));

        if (utils.isFunction(onSuccess)) {
            onSuccess();
        }
    } catch (error) {
        yield put(fetchProperties.fetchPropertyLocalisationFailure(error, payload));

        if (utils.isFunction(onError)) {
            onError(error);
        }
    }
}

function* createPropertyAsync({ payload }) {
    const {
        formValues,
        onError,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/properties`;

        // Fetch the data.
        const response = yield apiClient.create(url, formValues.base);

        // Send the data to the success action.
        yield put(createProperties.createPropertyLocalisation(payload, response));
    } catch (error) {
        // Trigger the error action.
        yield put(createProperties.createPropertyFailure(error));

        yield onError(error);
    }
}

function* createPropertyLocalisationAsync({ payload, response }) {
    const {
        formValues,
        onError,
        onSuccess,
    } = payload;

    const {
        id,
    } = response;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/properties/${ id }/localisation`;

        if (!utils.isEmpty(formValues.localisation)) {
            yield apiClient.update(url, formValues.localisation);
        }

        // Send the data to the success action.
        yield put(createProperties.createPropertyLocalisationSuccess(payload, response));

        yield onSuccess(response);
    } catch (error) {
        // Trigger the error action.
        yield put(createProperties.createPropertyLocalisationFailure(error));

        yield onError(error);
    }
}

function* createPropertyLocalisationSuccessAsync() {
    yield put(showNotification('notifications.properties.create.success'));
}

function* updatePropertyAsync({ payload }) {
    const {
        formValues,
        id,
        onError,
        onSuccess,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/properties/${ id }`;

        // Patch the data.
        const response = yield apiClient.patch(url, formValues.system);

        // Send the data to the success action.
        yield put(updateProperties.updatePropertyLocalisation(payload, response));

        yield onSuccess(response);
    } catch (error) {
        // Trigger the error action.
        yield put(updateProperties.updatePropertyFailure(error));

        yield onError(error);
    }
}

function* updatePropertyLocalisationAsync({ payload, response }) {
    const {
        formValues,
        id,
        onError,
        onSuccess,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/properties/${ id }/localisation`;

        if (!utils.isEmpty(formValues.localisation)) {
            yield apiClient.update(url, formValues.localisation);
        }

        // Send the data to the success action.
        yield put(updateProperties.updatePropertyLocalisationSuccess(payload, response));

        yield onSuccess();
    } catch (error) {
        // Trigger the error action.
        yield put(updateProperties.updatePropertyLocalisationFailure(error));

        yield onError(error);
    }
}

function* updatePropertyLocalisationSuccessAsync() {
    yield put(showNotification('notifications.properties.update.success'));
}

function* deletePropertyAsync({ payload }) {
    try {

        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/properties/${ payload.id }`;

        // Delete the data.
        yield apiClient.delete(url);

        yield put(deleteProperties.deletePropertySuccess(payload));
    } catch (error) {
        // Trigger the error action.
        yield put(deleteProperties.deletePropertyFailure(error));
    }
}

function* deletePropertySuccessAsync() {
    yield put(showNotification('notifications.properties.delete.success', 'success'));
}

function* failureMessage({ error }) {
    const { message } = error.response;

    if (!message) {
        return;
    }

    yield put(showNotification(message, 'warning'));
}

export default function* propertiesSagas() {
    yield [
        takeEvery(types.FETCH_PROPERTIES, fetchPropertiesAsync),
        takeEvery(types.FETCH_PROPERTIES_FAILURE, failureMessage),

        takeEvery(types.FETCH_PROPERTY, fetchPropertyAsync),
        takeEvery(types.FETCH_PROPERTY_FAILURE, failureMessage),

        takeEvery(types.FETCH_PROPERTY_LOCALISATION, fetchPropertyLocalisationAsync),
        takeEvery(types.FETCH_PROPERTY_LOCALISATION_FAILURE, failureMessage),

        takeEvery(types.CREATE_PROPERTY, createPropertyAsync),
        takeEvery(types.CREATE_PROPERTY_FAILURE, failureMessage),

        takeEvery(types.CREATE_PROPERTY_LOCALISATION, createPropertyLocalisationAsync),
        takeEvery(types.CREATE_PROPERTY_LOCALISATION_FAILURE, failureMessage),
        takeEvery(types.CREATE_PROPERTY_LOCALISATION_SUCCESS, createPropertyLocalisationSuccessAsync),

        takeEvery(types.UPDATE_PROPERTY, updatePropertyAsync),
        takeEvery(types.UPDATE_PROPERTY_FAILURE, failureMessage),

        takeEvery(types.UPDATE_PROPERTY_LOCALISATION, updatePropertyLocalisationAsync),
        takeEvery(types.UPDATE_PROPERTY_LOCALISATION_FAILURE, failureMessage),
        takeEvery(types.UPDATE_PROPERTY_LOCALISATION_SUCCESS, updatePropertyLocalisationSuccessAsync),

        takeEvery(types.DELETE_PROPERTY, deletePropertyAsync),
        takeEvery(types.DELETE_PROPERTY_FAILURE, failureMessage),
        takeEvery(types.DELETE_PROPERTY_SUCCESS, deletePropertySuccessAsync),
    ];
}



// WEBPACK FOOTER //
// ./src/store/sagas/propertiesSagas.js